import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { resolveHttpError, withRequestStatus } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { switchMap } from 'rxjs';
import { AuthApiClient } from '../../services';
import { forgotPasswordEvents } from './forgot-password.events';

/**
 * Signal store for managing the forgot password flow.
 *
 * Handles user requests to initiate password recovery by sending
 * a reset link to their email address.
 *
 * @example
 * const forgotPassword = inject(ForgotPasswordStore);
 * const dispatch = injectDispatch(forgotPasswordEvents);
 *
 * // In a component
 * dispatch.forgotPasswordRequested('user@example.com');
 *
 * // Watch forgot password state
 * effect(() => {
 *   if (forgotPassword.isSuccess() === 'success') {
 *     // Show confirmation message to user
 *   }
 * });
 */
export const ForgotPasswordStore = signalStore(
  withRequestStatus(),
  withReducer(
    on(forgotPasswordEvents.forgotPasswordRequested, () => ({
      requestStatus: 'loading',
    })),
    on(forgotPasswordEvents.forgotPasswordSucceeded, () => ({
      requestStatus: 'success',
    })),
    on(forgotPasswordEvents.forgotPasswordFailed, ({ payload }) => ({
      requestStatus: { error: payload.error },
    }))
  ),
  withEventHandlers(
    (_, events = inject(Events), authService = inject(AuthApiClient)) => ({
      forgotPassword$: events
        .on(forgotPasswordEvents.forgotPasswordRequested)
        .pipe(
          switchMap(({ payload }) =>
            authService.forgotPassword(payload).pipe(
              mapResponse({
                next: () => forgotPasswordEvents.forgotPasswordSucceeded(),
                error: (error: HttpErrorResponse) =>
                  forgotPasswordEvents.forgotPasswordFailed({
                    error: resolveHttpError(error).code,
                  }),
              })
            )
          )
        ),
    })
  )
);
